<template>
    <div class="result-table">
        <div v-if="store.apiErrorMessage" class="result-table__message">
            {{ store.apiErrorMessage }}
        </div>
        <div v-else-if="store.searchResult?.results != null && store.searchResult.results.length > 0">
            <div class="result-table__list"
                v-if="store.searchResult?.results != null && store.searchResult.results.length > 0">
                <table>
                    <thead>
                        <tr>
                            <th class="column" v-for="(config, dataKey) in columnConfig" :key="dataKey"
                                :class="{ 'result-table__column--hidden': !config.visible }">
                                <div>
                                    <strong @click="sortData(dataKey)">{{ config.displayName }}</strong>
                                    <span v-if="dataKey === store.sorting.dataKey">{{ !store.sorting.descending ?
                                        '↑' : '↓' }}</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <SearchResult v-for="hit in store.displayedResults" :key="hit.id" :result="hit"
                            :columnConfig="columnConfig" />
                    </tbody>
                </table>
            </div>
            <div v-if="noVisibleColumns" class="result-table__message">
                Du måste välja minst en kolumn för att visa sökresultat.
            </div>
            <div class="result-table__bottom">
                <div class="result-table__displayed-results">
                    <strong>{{ store.displayedResults.length }}</strong> av {{ store.searchResult?.results.length }} träffar
                </div>
                <Pagination />
            </div>
        </div>
        <div v-else class="result-table__message">
            Inget resultat hittades
        </div>
    </div>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue';
import { columnConfig, IColumnItem, initialVisibility } from '@/interfaces/search/IQueryColumn';
import { useFilterStore } from "@/stores/filterStore";
import { useFilterApply } from '@/services/filter-apply-service';
import { useSearch } from '@/services/search-service';
import { useStore } from "@/stores/store";

import Pagination from '@/components/pagination/Pagination.vue';
import SearchResult from '@/components/searchresult/SearchResult.vue';


const filterStore = useFilterStore();
const { applyFilters } = useFilterApply();
const { sortSearchResult } = useSearch();
const store = useStore();

const noVisibleColumns = computed(() => {
    return Object.values(columnConfig).every((config: IColumnItem) => !config.visible);
});

watch([() => store.mapVersion, () => filterStore.filterPanelOpen], ([newMapVersion, newFilterPanelOpen], [oldMapVersion, oldFilterPanelOpen]) => {
    if (newMapVersion === false || newFilterPanelOpen !== oldFilterPanelOpen) {
        adjustTableColumns();
    }
});

const sortData = (key: string) => {
    if (store.sorting.dataKey != key) {
        store.sorting.descending = true;
        store.sorting.dataKey = key;
        sortSearchResult(key, store.sorting.descending);
    }

    else if (store.sorting.dataKey == key && store.sorting.descending == true) {
        store.sorting.descending = false;
        sortSearchResult(key, store.sorting.descending);
    }

    else {
        store.sorting.descending = true;
        store.sorting.dataKey = null;
        store.searchResult = store.originalSearchResult;
        applyFilters();
    }

    store.currentPage = 0;
};

function adjustTableColumns() {
  const element = document.getElementById('container');
  if (!element) return;

  let availableWidth = element.offsetWidth - (filterStore.filterPanelOpen ? 400 : 0);
  const maxColumnWidth = 300;
  let maxColumns = Math.floor(availableWidth / maxColumnWidth);

  const visibleColumns = Object.entries(columnConfig)
    .filter(([key, cfg]) => cfg.visible)
    .sort((a, b) => a[1].priority - b[1].priority);

  const autoColumns = Object.entries(columnConfig)
    .filter(([key]) => initialVisibility[key]) // only those that were initially true
    .sort((a, b) => a[1].priority - b[1].priority);

  if (visibleColumns.length > maxColumns) {
    // hide all auto columns
    autoColumns.forEach(([key]) => {
      columnConfig[key].visible = false;
    });

    // show the highest-priority ones, but only from initial true set
    autoColumns.slice(0, maxColumns).forEach(([key]) => {
      columnConfig[key].visible = true;
    });
  } else {
    // make sure all initial-true columns that fit are visible
    autoColumns.forEach(([key], index) => {
      columnConfig[key].visible = index < maxColumns;
    });
  }
}
</script>
